export const WORLD_CUP_CODE = "WC";
export const WORLD_CUP_PROVIDER_LEAGUE_ID = 1;
export const WORLD_CUP_SEASON = 2026;

const firstDefined = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

export const normalizeWorldCupTeamName = (value = "") =>
  String(value)
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]/g, "");

export const isWorldCupCompetition = (value = {}) => {
  const source = typeof value === "string" ? { code: value, name: value } : value || {};
  const code = String(
    firstDefined(source.league_code, source.code, source.comp_code, source.comp, "")
  )
    .trim()
    .toUpperCase();
  const providerId = Number(
    firstDefined(
      source.provider_league_id,
      source.league_id,
      source.api_league_id,
      source.id,
      source.league?.id,
      0
    )
  );
  const name = String(
    firstDefined(
      source.league_name,
      source.competition,
      source.comp,
      source.name,
      source.league?.name,
      ""
    )
  ).toLowerCase();

  return code === WORLD_CUP_CODE || providerId === WORLD_CUP_PROVIDER_LEAGUE_ID || name.includes("world cup");
};

export const canonicalWorldCupContext = (value = {}) =>
  isWorldCupCompetition(value)
    ? {
        league_code: WORLD_CUP_CODE,
        league_id: WORLD_CUP_PROVIDER_LEAGUE_ID,
        season: WORLD_CUP_SEASON,
      }
    : null;

export const isOfficialWorldCupGroup = (name = "") =>
  /^group\s+[a-l]$/i.test(String(name).trim());

const groupSortValue = (name = "") => {
  const match = String(name).trim().match(/^group\s+([a-l])$/i);
  return match ? match[1].toUpperCase().charCodeAt(0) - 65 : 100;
};

export const normalizeWorldCupStandingRow = (row = {}, index = 0) => {
  const all = firstDefined(row.all, row.overall, row.stats?.all, {});
  const gf = firstDefined(row.gf, row.goals_for, row.goals?.for, all.goals?.for, null);
  const ga = firstDefined(row.ga, row.goals_against, row.goals?.against, all.goals?.against, null);
  const numericGf = Number(gf);
  const numericGa = Number(ga);

  return {
    position: firstDefined(row.position, row.rank, row.pos, index + 1),
    team: firstDefined(
      row.team?.name,
      row.team_name,
      row.name,
      typeof row.team === "string" ? row.team : null,
      "Unknown"
    ),
    team_id: firstDefined(row.team_id, row.provider_team_id, row.team?.id, row.id, null),
    team_logo: firstDefined(row.team_logo, row.logo, row.team?.logo, row.team_badge, null),
    played: firstDefined(row.played, row.p, row.mp, all.played, null),
    win: firstDefined(row.win, row.w, all.win, null),
    draw: firstDefined(row.draw, row.d, all.draw, null),
    lose: firstDefined(row.lose, row.loss, row.l, all.lose, null),
    gf,
    ga,
    goal_difference: firstDefined(
      row.goal_difference,
      row.goalsDiff,
      row.gd,
      Number.isFinite(numericGf) && Number.isFinite(numericGa) ? numericGf - numericGa : null
    ),
    points: firstDefined(row.points, row.pts, null),
    form: firstDefined(row.form, row.recent_form, ""),
    group: firstDefined(row.group, row.stage_group, ""),
    description: firstDefined(row.description, row.note, ""),
  };
};

const makeGroup = (name, rows, index) => ({
  name: firstDefined(name, rows?.[0]?.group, `Group ${index + 1}`),
  rows: (Array.isArray(rows) ? rows : []).map(normalizeWorldCupStandingRow),
});

export const normalizeWorldCupGroups = (payload = {}) => {
  let groups = [];

  if (Array.isArray(payload?.table_groups) && payload.table_groups.length) {
    groups = payload.table_groups.map((group, index) =>
      makeGroup(firstDefined(group.group, group.name, group.label), group.table || group.rows, index)
    );
  } else {
    const nested = firstDefined(
      payload?.response?.[0]?.league?.standings,
      payload?.league?.standings,
      payload?.standings,
      []
    );

    if (Array.isArray(nested) && nested.length) {
      const nestedGroups = Array.isArray(nested[0]) ? nested : [nested];
      groups = nestedGroups.map((rows, index) =>
        makeGroup(firstDefined(rows?.[0]?.group, rows?.[0]?.stage_group), rows, index)
      );
    } else if (Array.isArray(payload?.table)) {
      const byGroup = new Map();
      payload.table.forEach((row) => {
        const groupName = firstDefined(row?.group, row?.stage_group, "");
        if (!groupName) return;
        if (!byGroup.has(groupName)) byGroup.set(groupName, []);
        byGroup.get(groupName).push(row);
      });
      groups = [...byGroup.entries()].map(([name, rows], index) => makeGroup(name, rows, index));
    }
  }

  return groups
    .filter((group) => isOfficialWorldCupGroup(group.name) && group.rows.length)
    .sort((a, b) => groupSortValue(a.name) - groupSortValue(b.name));
};

export const findWorldCupFixtureGroup = (groups, fixture = {}) => {
  const homeId = firstDefined(
    fixture.home_team_id,
    fixture.provider_home_team_id,
    fixture.home?.id,
    fixture.teams?.home?.id
  );
  const awayId = firstDefined(
    fixture.away_team_id,
    fixture.provider_away_team_id,
    fixture.away?.id,
    fixture.teams?.away?.id
  );
  const homeName = normalizeWorldCupTeamName(
    firstDefined(fixture.home_team, fixture.home?.name, fixture.teams?.home?.name, "")
  );
  const awayName = normalizeWorldCupTeamName(
    firstDefined(fixture.away_team, fixture.away?.name, fixture.teams?.away?.name, "")
  );

  return (Array.isArray(groups) ? groups : []).find((group) =>
    group.rows.some((row) => {
      if (homeId && String(row.team_id) === String(homeId)) return true;
      if (awayId && String(row.team_id) === String(awayId)) return true;
      const rowName = normalizeWorldCupTeamName(row.team);
      return rowName && (rowName === homeName || rowName === awayName);
    })
  );
};

export const buildWorldCupTeamGroupMap = (groups = []) => {
  const map = new Map();
  groups.forEach((group) => {
    group.rows.forEach((row) => {
      if (row.team_id) map.set(`id:${row.team_id}`, group.name);
      if (row.team) map.set(`name:${normalizeWorldCupTeamName(row.team)}`, group.name);
    });
  });
  return map;
};
